import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { wsChannel } from '@xdocs/schemas';
import type { DocumentVersion, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseCurrentVersionResult {
  version: DocumentVersion | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
}

/**
 * F67 editor bootstrap: loads the document's CURRENT version's content
 * (`version.get`) so the WYSIWYG surface has something to render, kept live
 * via `document:{documentId}` (a new version lands from any action —
 * generate/expand/cut/difficulty/translate/edit/restyle — §5.5).
 */
export function useCurrentVersion(documentId: string, versionId: string | null): UseCurrentVersionResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected' && versionId !== null;

  const query = useQuery<DocumentVersion, ErrorAlert>({
    queryKey: ['version', documentId, versionId],
    queryFn: () => requestTyped(wsClient, 'version.get', { documentId, versionId: versionId as string }),
    enabled,
  });

  useEffect(() => {
    const channel = wsChannel.document(documentId);
    return wsClient.subscribe(channel, () => {
      void queryClient.invalidateQueries({ queryKey: ['version', documentId] });
    });
  }, [wsClient, queryClient, documentId]);

  return {
    version: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.isError ? query.error : null,
    refetch: () => void query.refetch(),
  };
}
